import { THUMBNAIL } from "@/lib/constants/shared";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useRef, useState } from "react";
import { FiCheck, FiUpload, FiX } from "react-icons/fi";
import { IoMoveSharp } from "react-icons/io5";
import { MdAutoAwesome } from "react-icons/md";

interface ImageUploadProps {
  onFileSelect: (file: File | null) => void;
  currentImageUrl: string | null;
  onRemove?: () => void;
  aspectRatio: number;
  /** When provided, shows an "AI generate" button; the returned image goes through the crop flow. */
  onAiGenerate?: () => Promise<File | null>;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export function ImageUpload({
  onFileSelect,
  onRemove,
  currentImageUrl,
  aspectRatio,
  onAiGenerate,
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cropMode, setCropMode] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const file = files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = (file: File) => {
    setError(null);
    setImageLoaded(false);

    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds maximum limit of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Invalid file type. Allowed types: JPEG, PNG, WEBP, GIF");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setTempImageUrl(url);
      setCropMode(true);
    };
    reader.readAsDataURL(file);
    setSelectedFile(file);
  };

  const handleImageLoad = () => {
    if (!imageRef.current) return;

    const imgWidth = imageRef.current.naturalWidth;
    const imgHeight = imageRef.current.naturalHeight;
    const imgAspect = imgWidth / imgHeight;

    let cropWidth: number;
    let cropHeight: number;
    let x: number;
    let y: number;

    if (imgAspect > aspectRatio) {
      // Image is wider than 3:4
      cropHeight = imgHeight;
      cropWidth = cropHeight * aspectRatio;
      x = (imgWidth - cropWidth) / 2;
      y = 0;
    } else {
      // Image is taller than 3:4
      cropWidth = imgWidth;
      cropHeight = cropWidth / aspectRatio;
      x = 0;
      y = (imgHeight - cropHeight) / 2;
    }

    setCropArea({ x, y, width: cropWidth, height: cropHeight });
    setImageLoaded(true);
  };

  const handleCropMouseDown = (e: React.MouseEvent) => {
    if (!imageRef.current) return;
    setIsDraggingCrop(true);
    const rect = imageRef.current.getBoundingClientRect();
    const scaleX = imageRef.current.naturalWidth / rect.width;
    const scaleY = imageRef.current.naturalHeight / rect.height;
    setDragStart({
      x: (e.clientX - rect.left) * scaleX - cropArea.x,
      y: (e.clientY - rect.top) * scaleY - cropArea.y,
    });
  };

  const handleCropMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingCrop || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const scaleX = imageRef.current.naturalWidth / rect.width;
    const scaleY = imageRef.current.naturalHeight / rect.height;

    let newX = (e.clientX - rect.left) * scaleX - dragStart.x;
    let newY = (e.clientY - rect.top) * scaleY - dragStart.y;

    // Constrain to image bounds
    newX = Math.max(0, Math.min(newX, imageRef.current.naturalWidth - cropArea.width));
    newY = Math.max(0, Math.min(newY, imageRef.current.naturalHeight - cropArea.height));

    setCropArea((prev) => ({ ...prev, x: newX, y: newY }));
  };

  const handleCropMouseUp = () => {
    setIsDraggingCrop(false);
  };

  const handleCropConfirm = () => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = cropArea.width;
    canvas.height = cropArea.height;

    ctx.drawImage(
      imageRef.current,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      cropArea.width,
      cropArea.height
    );

    const isPng = selectedFile?.type === "image/png";
    const outputMimeType = isPng ? "image/png" : "image/jpeg";
    const outputExtension = isPng ? "png" : "jpg";
    const outputQuality = isPng ? undefined : 0.95;

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const originalName = selectedFile?.name || `cropped.${outputExtension}`;
          const nameWithoutExt = originalName.replace(/\.[^.]+$/, "");
          const croppedFile = new File([blob], `${nameWithoutExt}.${outputExtension}`, {
            type: outputMimeType,
          });

          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
          setCropMode(false);
          setTempImageUrl(null);
          onFileSelect(croppedFile);
        }
      },
      outputMimeType,
      outputQuality
    );
  };

  const handleCropCancel = () => {
    setCropMode(false);
    setTempImageUrl(null);
    setSelectedFile(null);
    setImageLoaded(false);
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setError(null);
    onFileSelect(null);
    if (onRemove) {
      onRemove();
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleAiGenerate = async () => {
    if (!onAiGenerate || isGenerating) return;
    setIsGenerating(true);
    try {
      const file = await onAiGenerate();
      if (file) {
        // AI-generated images are used as-is (no crop), matching the original vocab-card
        // generation flow which stores the raw square image.
        setError(null);
        setPreviewUrl(URL.createObjectURL(file));
        setSelectedFile(file);
        onFileSelect(file);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const aspectRatioClass =
    aspectRatio === THUMBNAIL.projectAspectRatio
      ? THUMBNAIL.projectAspectRatioClass
      : THUMBNAIL.lessonAspectRatioClass;

  return (
    <div className="space-y-4">
      {/* Crop Mode */}
      {cropMode && tempImageUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-surface rounded-3xl p-8 max-h-[90vh] overflow-auto shadow-2xl border border-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="h4">Crop Image</h3>
              <div className="bg-purple-500/20 text-purple-400 text-xs font-semibold px-3 py-1 rounded-full">
                {THUMBNAIL.projectAspectRatioString}
              </div>
            </div>

            <div
              className="relative max-w-full rounded-2xl overflow-hidden shadow-lg flex flex-col"
              onMouseMove={handleCropMouseMove}
              onMouseUp={handleCropMouseUp}
              onMouseLeave={handleCropMouseUp}
            >
              <img
                ref={imageRef}
                src={tempImageUrl}
                alt="Crop preview"
                className="max-w-full h-auto"
                style={{ maxHeight: "60vh" }}
                onLoad={handleImageLoad}
              />
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-secondary-200 font-semibold flex items-center gap-2">
                    <div className="w-5 h-5 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    Loading image...
                  </div>
                </div>
              )}
              {imageLoaded && imageRef.current && (
                <div
                  className="absolute border-4 border-primary-500 cursor-move shadow-2xl"
                  style={{
                    left: `${(cropArea.x / imageRef.current.naturalWidth) * 100}%`,
                    top: `${(cropArea.y / imageRef.current.naturalHeight) * 100}%`,
                    width: `${(cropArea.width / imageRef.current.naturalWidth) * 100}%`,
                    height: `${(cropArea.height / imageRef.current.naturalHeight) * 100}%`,
                    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                  }}
                  onMouseDown={handleCropMouseDown}
                >
                  <div className="absolute inset-0 border-2 border-dashed border-primary-400 opacity-70" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface bg-opacity-90 text-primary-500 text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg pointer-events-none border border-gray-800">
                    <IoMoveSharp fontSize={25} />
                  </div>
                </div>
              )}
            </div>

            <p className="caption mt-5 text-center font-medium">
              💡 Drag the frame to select your perfect crop
            </p>

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={handleCropCancel}>
                <FiX className="h-5 w-5" />
                Cancel
              </Button>
              <Button variant="primary" onClick={handleCropConfirm} className="flex-1">
                <FiCheck className="h-5 w-5" />
                Crop Image
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for cropping */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Preview or Upload Zone */}
      {previewUrl ? (
        <div className={`relative ${aspectRatioClass} flex items-center justify-center`}>
          <img src={previewUrl} alt="Preview" className="w-full h-full object-cover rounded-lg" />
          <div className="absolute top-2 right-2 flex gap-2">
            <IconButton
              variant="primary"
              size="sm"
              onClick={handleClick}
              className="rounded-full shadow-lg"
              aria-label="Change image"
            >
              <FiUpload className="h-4 w-4" />
            </IconButton>
            <IconButton
              variant="danger"
              size="sm"
              onClick={handleRemove}
              className="rounded-full shadow-lg"
              aria-label="Remove image"
            >
              <FiX className="h-4 w-4" />
            </IconButton>
          </div>
          {selectedFile && (
            <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
              {selectedFile.name}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFileInput}
            className="hidden"
            aria-label="Upload image"
          />
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`${
            isDragging
              ? "border-primary-500 bg-primary-500/10"
              : "border-gray-800 bg-surface hover:border-gray-700"
          } cursor-pointer ${aspectRatioClass} rounded-lg border-2 border-dashed p-8 transition-colors flex items-center justify-center`}
        >
          <div className="text-center">
            <FiUpload className="mx-auto h-12 w-12 text-secondary-200" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-secondary-200">
              Click to upload or drag and drop
            </p>
            <p className="caption mt-1">
              JPEG, PNG, WEBP, GIF (Max {MAX_FILE_SIZE / 1024 / 1024}MB)
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFileInput}
            className="hidden"
            aria-label="Upload image"
          />
        </div>
      )}

      {/* AI Generate */}
      {onAiGenerate && (
        <Button
          variant="secondary"
          onClick={handleAiGenerate}
          disabled={isGenerating}
          className="w-full"
        >
          <MdAutoAwesome className="h-5 w-5" />
          {isGenerating ? "Generating..." : "Generate with AI"}
        </Button>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-error-500/10 border border-error-500/20 p-3">
          <p className="text-sm text-error-500">{error}</p>
        </div>
      )}
    </div>
  );
}
